import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { permissions } from 'features/access-control/MOCK_PERMISSIONS';
import { authAPI } from '../../api/authAPI';
import { AppThunk } from '../store';

interface UsersState {
  user: object | null;
  loading: boolean;
  error: string | null;
  authenticated: boolean;
  access: object | null;
  bookmarks: any;
  loggingOut: boolean;
}

const initialState: UsersState = {
  user: null,
  loading: true,
  loggingOut: false,
  error: null,
  authenticated: false,
  access: permissions,
  bookmarks: [],
};

function startLoading(state: UsersState) {
  state.loading = true;
}

function loadingFailed(state: UsersState, action: PayloadAction<string>) {
  state.loading = false;
  state.error = action.payload;
}

const slice = createSlice({
  name: 'users',
  initialState: initialState,
  reducers: {
    getAuthStart: startLoading,
    getUserStart: startLoading,
    getAuthSuccess(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
      state.error = null;
      state.loggingOut = false;
      state.authenticated = payload;
    },
    getAuthFailure: loadingFailed,
    getUserFailure: loadingFailed,
    getUserSuccess(state, { payload }: PayloadAction<object>) {
      // state.loading = false;
      state.error = null;
      state.user = payload;
      state.loggingOut = false;
    },
    getPermissionsSuccess(state, { payload }: PayloadAction<object>) {
      state.loading = false;
      state.access = payload;
    },
    logoutUser(state) {
      state.user = null;
    },
    setUserPermissions(state, { payload }: PayloadAction<object>) {
      state.access = payload;
      authAPI.updateUserPermissions(state.user, payload);
      localStorage.setItem('permissions', JSON.stringify(state.access));
    },
    getBookmarksSuccess(state, { payload }: PayloadAction<object>) {
      state.bookmarks = payload;
    },
    createBookmark(state, { payload }: PayloadAction<object>) {
      const { bookmarks, user } = state;
      const newBookmarks = [...bookmarks, payload];
      state.bookmarks = newBookmarks;
      authAPI.setUserBookmarks(user, newBookmarks);
    },

    removeBookmark(state, { payload }: PayloadAction<{ refid: string }>) {
      const { bookmarks, user } = state;
      const arr = bookmarks.filter(
        (bookmark: { refid: string }) => bookmark.refid !== payload.refid
      );

      state.bookmarks = arr;
      authAPI.setUserBookmarks(user, arr);
    },

    setLogout(state, { payload }: PayloadAction<boolean>) {
      state.loggingOut = payload;
    },
  },
});

export const {
  getAuthStart,
  getAuthFailure,
  getAuthSuccess,
  getUserFailure,
  getUserStart,
  getUserSuccess,
  logoutUser,
  setUserPermissions,
  getPermissionsSuccess,
  setLogout,
} = slice.actions;

export const fetchAuthStatus = (): AppThunk => async (dispatch) => {
  try {
    dispatch(getAuthStart());
    const status = await authAPI.fetchAuthStatus();
    dispatch(getAuthSuccess(status.data));
  } catch (error: any) {
    dispatch(getAuthFailure(error.toString()));
  }
};

export const fetchUser = (): AppThunk => async (dispatch) => {
  try {
    dispatch(getUserStart());
    const user = await authAPI.fetchUser();
    dispatch(getUserSuccess(user.data));
    const permissions = await authAPI.getUserPermissions(user.data);
    // const bookmarks = await authAPI.getUserBookmarks(user.data);

    // dispatch(getBookmarksSuccess(bookmarks.data));
    // console.log('bookmarks.data', bookmarks.data);

    if (!permissions.data) {
      const { data } = await authAPI.createUserPermissions(user.data);
      dispatch(getPermissionsSuccess(data.access));
    } else {
      const { data } = permissions;
      dispatch(getPermissionsSuccess(data.access));
    }
  } catch (error: any) {
    dispatch(getUserFailure(error.toString()));
  }
};

export default slice.reducer;
